import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DeletedUsersFilters, DeletedUsersSortBy, DeletedUsersTable, userModel } from 'entities/user';

import { EUserDeletionSource, GetDeletedUsersParams } from 'shared/api';
import { useDebouncedSearch } from 'shared/lib';

const parseSource = (raw: string | null): EUserDeletionSource | '' =>
  Object.values(EUserDeletionSource).includes(raw as EUserDeletionSource) ? (raw as EUserDeletionSource) : '';

const parseSortBy = (raw: string | null): DeletedUsersSortBy => (raw === 'email' ? 'email' : 'deletedAt');

const parseSortOrder = (raw: string | null): 'asc' | 'desc' => (raw === 'asc' ? 'asc' : 'desc');

const parseIntStrict = (raw: string | null): number | null => (raw && /^\d+$/.test(raw) ? parseInt(raw, 10) : null);

const parsePage = (raw: string | null): number => {
  const n = parseIntStrict(raw);
  return n !== null && n > 0 ? n : 1;
};

const LIMIT_OPTIONS = [25, 50, 100];

const parseLimit = (raw: string | null): number => {
  const n = parseIntStrict(raw);
  return n !== null && LIMIT_OPTIONS.includes(n) ? n : 25;
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Кроме формата проверяем существование даты round-trip'ом: '2026-02-31' форму проходит,
// но Date нормализует его в другой день — такое значение отбрасываем
const parseApiDate = (raw: string | null): string => {
  if (!raw || !DATE_RE.test(raw)) return '';
  const [y, m, d] = raw.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d ? raw : '';
};

// YYYY-MM-DD → Date в локальной полуночи (для DatePicker); бек трактует эти даты как UTC-календарные дни
const toPickerDate = (s: string): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const toApiDate = (date: Date | null): string => {
  if (!date || Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// Стейт таба живёт в URL (паттерн /translations): Back/Forward обновляет контролы,
// локален только сырой текст поиска до дебаунса.
export const DeletedUsersTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filter = searchParams.get('filter') ?? '';
  const source = parseSource(searchParams.get('source'));
  const from = parseApiDate(searchParams.get('from'));
  const to = parseApiDate(searchParams.get('to'));
  const sortBy = parseSortBy(searchParams.get('sortBy'));
  const sortOrder = parseSortOrder(searchParams.get('sortOrder'));
  const page = parsePage(searchParams.get('page'));
  const rowsPerPage = parseLimit(searchParams.get('limit'));

  const [localFilter, setLocalFilter] = useState(filter);
  const debouncedFilter = useDebouncedSearch(localFilter, 250);

  // Синхронно обновляемый снимок параметров: два patchParams в одном тике
  // (дебаунс-таймер + клик по контролу) не затирают друг друга — react-router
  // передаёт в функциональный setSearchParams параметры своего рендера, не очередь.
  const paramsRef = useRef(searchParams);
  useEffect(() => {
    paramsRef.current = searchParams;
  }, [searchParams]);

  const patchParams = useCallback(
    (patch: Record<string, string | null>) => {
      const next = new URLSearchParams(paramsRef.current);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      });
      paramsRef.current = next;
      setSearchParams(next, { replace: true });
    },
    [setSearchParams],
  );

  // Внешняя смена filter в URL (Back/Forward, прямая навигация) перекрывает
  // недописанный локальный ввод — иначе отложенный дебаунс перезатёр бы навигацию.
  const prevUrlFilter = useRef(filter);
  useEffect(() => {
    if (filter !== prevUrlFilter.current) {
      prevUrlFilter.current = filter;
      setLocalFilter(filter);
    }
  }, [filter]);

  // Коммит локального ввода в URL — только когда дебаунс догнал текущий ввод
  useEffect(() => {
    if (debouncedFilter !== localFilter || debouncedFilter === filter) return;
    prevUrlFilter.current = debouncedFilter;
    patchParams({ filter: debouncedFilter, page: null });
  }, [debouncedFilter, localFilter, filter, patchParams]);

  // Параметры запроса читаем из закоммиченного URL (не из debouncedFilter):
  // смена фильтра и сброс страницы попадают в запрос одним атомарным переходом
  const params = useMemo<GetDeletedUsersParams>(
    () => ({
      filter: filter || undefined,
      source: source || undefined,
      from: from || undefined,
      to: to || undefined,
      sortBy,
      sortOrder,
      page,
      limit: rowsPerPage,
    }),
    [filter, source, from, to, sortBy, sortOrder, page, rowsPerPage],
  );

  const { data, isFetching, isError } = userModel.useDeletedUsers(params);
  const deletedUsers = data?.data.data;
  const pagination = data?.data.pagination ?? null;

  // page за пределами totalPages (например, из руками набранного URL) прижимаем
  // к последней валидной странице, когда ответ раскрыл реальный размер выборки
  useEffect(() => {
    if (!pagination) return;
    const lastPage = Math.max(pagination.totalPages, 1);
    if (page > lastPage) patchParams({ page: lastPage > 1 ? String(lastPage) : null });
  }, [pagination, page, patchParams]);

  const handleSourceChange = useCallback(
    (value: EUserDeletionSource | '') => patchParams({ source: value, page: null }),
    [patchParams],
  );

  const handleFromChange = useCallback(
    (date: Date | null) => patchParams({ from: toApiDate(date), page: null }),
    [patchParams],
  );

  const handleToChange = useCallback(
    (date: Date | null) => patchParams({ to: toApiDate(date), page: null }),
    [patchParams],
  );

  const handleSortChange = useCallback(
    (columnId: DeletedUsersSortBy) => {
      if (sortBy === columnId) {
        patchParams({ sortOrder: sortOrder === 'asc' ? 'desc' : 'asc', page: null });
      } else {
        // Дефолтное направление как у бека: по дате — свежие сверху, по email — по алфавиту
        patchParams({ sortBy: columnId, sortOrder: columnId === 'deletedAt' ? 'desc' : 'asc', page: null });
      }
    },
    [patchParams, sortBy, sortOrder],
  );

  const handlePageChange = useCallback((p: number) => patchParams({ page: String(p) }), [patchParams]);

  const handleRowsPerPageChange = useCallback(
    (n: number) => patchParams({ limit: String(n), page: null }),
    [patchParams],
  );

  return (
    <Box display="flex" flexDirection="column">
      <DeletedUsersFilters
        filter={localFilter}
        source={source}
        from={toPickerDate(from)}
        to={toPickerDate(to)}
        totalCount={pagination?.total ?? 0}
        isFetching={isFetching}
        onFilterChange={setLocalFilter}
        onSourceChange={handleSourceChange}
        onFromChange={handleFromChange}
        onToChange={handleToChange}
      />

      <DeletedUsersTable
        users={deletedUsers}
        pagination={pagination}
        page={page}
        rowsPerPage={rowsPerPage}
        sortBy={sortBy}
        sortOrder={sortOrder}
        isLoading={isFetching}
        isError={isError}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onSortChange={handleSortChange}
      />
    </Box>
  );
};
